import {
  ActionIcon,
  createStyles,
  Group,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { AnimatePresence, motion, MotionStyle } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { ReactComponent as CollapseRightIcon } from '@portals/icons/linear/collapse-right.svg';
import { SmartTable } from '@portals/table';
import {
  CurrencyCode,
  SubscriptionInvoiceItemType,
  TableColumn,
} from '@portals/types';
import { formatPrice, mediumDateFormatter } from '@portals/utils';

import { InvoicePaymentStatusBadge } from './InvoicePaymentStatusBadge';

interface PaymentsHistoryPanelProps {
  isOpen: boolean;
  currencyCode: CurrencyCode;
  usageBasedDisplayName: string;
  onClose: () => void;
  invoiceItems: SubscriptionInvoiceItemType[];
  isFetching: boolean;
}

export function PaymentsHistoryPanel({
  invoiceItems,
  isFetching,
  isOpen,
  currencyCode,
  usageBasedDisplayName,
  onClose,
}: PaymentsHistoryPanelProps) {
  const { classes } = useStyles();

  const [wrapperStyles, setWrapperStyles] = useState<MotionStyle>();

  useEffect(function calcWrapperDimensions() {
    function resizeListener() {
      const pageContentElement = document.getElementById('page-content');
      const pageContentRect = pageContentElement?.getBoundingClientRect?.();
      const detailsPanelWidth = document?.getElementsByClassName?.(
        'table-details-panel-container'
      )?.[0]?.clientWidth;

      if (!pageContentElement || !pageContentRect) return;

      setWrapperStyles({
        // +1px for the details panel left border
        right: detailsPanelWidth + 1,
        maxWidth: pageContentRect.width - detailsPanelWidth,
      });
    }

    // Initial calculation
    resizeListener();

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  const columns: TableColumn<SubscriptionInvoiceItemType>[] = [
    {
      dataField: 'created_at',
      text: 'Creation date',
      formatter: (_, { created_at }) => mediumDateFormatter(created_at, 'N/A'),
    },
    {
      dataField: 'meter_delta',
      text: usageBasedDisplayName,
      formatter: (_, { meter_start, meter_end }) => {
        return meter_end - meter_start;
      },
    },
    {
      dataField: 'price_in_scu',
      text: 'Total paid',
      formatter: (_, { price_in_scu }) =>
        formatPrice({
          value: price_in_scu,
          currencyCode: currencyCode,
        }),
    },
    {
      dataField: 'status',
      text: 'Payment status',
      formatter: (_, { status }) => (
        <InvoicePaymentStatusBadge status={status} />
      ),
    },
  ];

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'linear' }}
          className={classes.paymentHistoryPanel}
          style={wrapperStyles}
        >
          <Stack p="xxl" spacing="xxl" h="100%">
            <LoadingOverlay visible={isFetching} />

            <Group position="apart">
              <Text size="lg">Payments History</Text>

              <ActionIcon onClick={onClose}>
                <CollapseRightIcon />
              </ActionIcon>
            </Group>

            <SmartTable<SubscriptionInvoiceItemType>
              noHeader
              name="subscription.invoice_items"
              keyField="id"
              columns={columns}
              data={invoiceItems}
            />
          </Stack>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const useStyles = createStyles((theme) => ({
  paymentHistoryPanel: {
    position: 'absolute',
    top: 0,
    width: 800,
    height: '100%',
    backgroundColor: theme.white,
    boxShadow: '6px 0px 41px 0px rgba(0, 0, 0, 0.13)',
    clipPath: 'inset(0 0 0 -41px)',
  },
}));
